import { useEffect, useRef, useState } from "react";

/**
 * Renders an image with the light/white background removed client-side via canvas.
 * Any pixel whose R,G,B are all above `threshold` becomes fully transparent, and
 * near-threshold pixels get feathered alpha to soften edges.
 */
export default function TransparentImage({
  src,
  alt = "",
  threshold = 235,
  softness = 25,
  className = "",
  ...rest
}) {
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      try {
        const canvas = canvasRef.current || document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const d = imgData.data;
        for (let i = 0; i < d.length; i += 4) {
          const r = d[i], g = d[i + 1], b = d[i + 2];
          const minVal = Math.min(r, g, b);
          if (minVal >= threshold) {
            d[i + 3] = 0;
          } else if (minVal >= threshold - softness) {
            const alpha = 1 - (minVal - (threshold - softness)) / softness;
            d[i + 3] = Math.round(d[i + 3] * alpha);
          }
        }
        ctx.putImageData(imgData, 0, 0);
        setDataUrl(canvas.toDataURL("image/png"));
      } catch (err) {
        setFailed(true);
      }
    };
    img.onerror = () => setFailed(true);
    img.src = src;
    return () => { cancelled = true; };
  }, [src, threshold, softness]);

  if (failed) {
    return <img src={src} alt={alt} className={className} {...rest} />;
  }
  return (
    <>
      <canvas ref={canvasRef} className="hidden" aria-hidden />
      {dataUrl && <img src={dataUrl} alt={alt} className={className} {...rest} />}
    </>
  );
}
